import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { setTokenProvider } from '@/lib/api';
import { env } from '@/config/env';
import { decodeJwtPayload, type SessionUser } from './claims';
import { createCognitoProvider } from './cognito';
import { createDevProvider } from './devProvider';
import {
  isTokenExpiring,
  resolveAuthMode,
  type AuthMode,
  type AuthProvider,
  type AuthSession,
  type SignInResult,
} from './provider';

/**
 * Authentication state for the SPA.
 *
 * Real auth is Amazon Cognito (via {@link createCognitoProvider}); when no pool
 * is configured we fall back to a clearly-labeled DEV mock so `pnpm dev:web`
 * runs with no backend (see {@link createDevProvider}). The active provider is
 * chosen ONCE at startup by {@link resolveAuthMode}.
 *
 * This layer is NOT security: the AWS backend verifies the Cognito JWT on every
 * request and is the sole authority on access. We only inject the current ID
 * token into the API client and drive UX (which portal to show).
 */
export interface Session {
  /** Current Cognito **ID token** (JWT) injected as the API bearer. */
  token: string;
  user: SessionUser;
}

/** Bootstrap lifecycle: `loading` until the first session restore resolves. */
export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  session: Session | null;
  status: AuthStatus;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Which provider is active — `dev` shows the "modo desarrollo" affordances. */
  mode: AuthMode;
  /** Sign in with email + password. Returns the provider result (handles new-password flow). */
  signIn: (input: { email: string; password: string }) => Promise<SignInResult>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Create the configured provider once. Logs a one-line notice of the active mode. */
function createConfiguredProvider(): AuthProvider {
  const mode = resolveAuthMode(
    {
      userPoolId: env.cognitoUserPoolId,
      userPoolClientId: env.cognitoUserPoolClientId,
    },
    import.meta.env.PROD,
  );
  if (mode === 'cognito') {
    console.info('[auth] Cognito habilitado — autenticación real contra el User Pool.');
    return createCognitoProvider({
      userPoolId: env.cognitoUserPoolId,
      userPoolClientId: env.cognitoUserPoolClientId,
    });
  }
  console.info('[auth] modo desarrollo — sesión simulada (sin Cognito). No usar en producción.');
  return createDevProvider();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  // The provider instance is stable for the app's lifetime.
  const providerRef = useRef<AuthProvider>();
  if (!providerRef.current) providerRef.current = createConfiguredProvider();
  const provider = providerRef.current;

  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');

  // Hold the latest session in a ref so the (once-registered) token provider
  // reads the current token without re-registration on every render.
  const sessionRef = useRef<Session | null>(null);
  sessionRef.current = session;
  // Track expiry separately so the token provider can decide when to refresh.
  const expiresAtRef = useRef<number>(0);

  const applySession = useCallback((next: AuthSession | null) => {
    if (next) {
      sessionRef.current = { token: next.idToken, user: next.user };
      expiresAtRef.current = next.expiresAt;
      setSession({ token: next.idToken, user: next.user });
      setStatus('authenticated');
    } else {
      sessionRef.current = null;
      expiresAtRef.current = 0;
      setSession(null);
      setStatus('unauthenticated');
    }
  }, []);

  // Register the API token provider exactly once. It is async: when the cached
  // token is near expiry it asks the provider for a fresh one (Cognito uses the
  // refresh token transparently), keeping every API call authenticated.
  useEffect(() => {
    setTokenProvider(async () => {
      const current = sessionRef.current;
      if (!current) return null;
      if (!isTokenExpiring(expiresAtRef.current)) return current.token;
      // Expired/near-expiry → refresh. On failure the session is dropped so the
      // next render redirects to /login rather than retrying a dead token.
      const refreshed = await provider.getValidIdToken();
      if (!refreshed) {
        applySession(null);
        return null;
      }
      // Cache the fresh token + its expiry so we don't re-enter this branch on
      // every subsequent request until the new token itself nears expiry.
      const exp = decodeJwtPayload(refreshed)?.exp;
      if (typeof exp === 'number') expiresAtRef.current = exp;
      sessionRef.current = { ...current, token: refreshed };
      return refreshed;
    });
  }, [provider, applySession]);

  // Restore any persisted session on first load (silent refresh if needed).
  useEffect(() => {
    let cancelled = false;
    void provider.restore().then((restored) => {
      if (!cancelled) applySession(restored);
    });
    return () => {
      cancelled = true;
    };
  }, [provider, applySession]);

  const signIn = useCallback<AuthContextValue['signIn']>(
    async (input) => {
      const result = await provider.signIn(input);
      if (result.status === 'ok') {
        // Drop any cached server-state from a previous session before applying.
        queryClient.clear();
        applySession(result.session);
      }
      return result;
    },
    [provider, applySession, queryClient],
  );

  const signOut = useCallback(async () => {
    await provider.signOut();
    applySession(null);
    queryClient.clear();
  }, [provider, applySession, queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      status,
      isAuthenticated: status === 'authenticated' && Boolean(session),
      isLoading: status === 'loading',
      mode: provider.mode,
      signIn,
      signOut,
    }),
    [session, status, provider.mode, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>.');
  return ctx;
}
